import classNames from 'classnames';
import { AllHtmlEntities } from 'html-entities';
import fp from 'lodash/fp';
import React from 'react';
import { graphql } from 'react-apollo';
import { Link } from 'react-router-dom';
import { Button, Col, Container, Row } from 'reactstrap';
import { branch, compose, mapProps, pure } from 'recompose';
import { noCapMap } from '../../../../common';
import { routes, socials } from '../../../../constants';
import { hideIfNoArtistData, notFoundIfNoArtistData } from '../../../higher-order';
import { InstagramFeed } from '../../../partials';
import { Embed, Icon } from '../../../proxy';
import './artist.css';
import artistQuery from './artist.gql';
import instagramQuery from './instagram.gql';

const Artist = ({
  artistData: {
    artist: {
      bio,
      currentLocation: {
        country
      },
      originalLocation,
      name,
      profileImage,
      socialLinks,
      otherImages,
      videos,
      partners,
      styles
    }
  },
  instagramData
}) => (
    <section className='artist'>
      <Container>
        <h1 className='my-4'>
          {name}
        </h1>
        <Row>
          <Col xs={12} lg={videos && videos.length ? 7 : 12}>
            <Row>
              <Col className='flex-center'>
                <img className='img-fluid z-depth-1-half' src={profileImage} alt={name} />
              </Col>
            </Row>
            {
              otherImages && otherImages.length ?
                <Row className='mt-3'>
                  {
                    fp.map((photo) =>
                      <Col key={photo} className='mb-4' xs={12} sm={6} md={3}>
                        <img className='img-fluid z-depth-1' src={photo} alt={name} />
                      </Col>
                    )(otherImages)
                  }
                </Row> :
                null
            }
            {
              partners && partners.length ?
                <Row>
                  <Col>
                    <h3>Partner{partners.length > 1 ? 's' : ''}</h3>
                    <Row>
                      {
                        fp.map(({ id, firstName, lastName, name, profileImage }) => (
                          <Col key={name} xs={12} md={6}>
                            <div className='testimonial-card'>
                              <Row noGutters>
                                <Col xs={12} sm={6}>
                                  <div className='avatar'>
                                    <Link to={routes.artist(id)}>
                                      <img
                                        className='rounded-circle img-responsive'
                                        src={profileImage}
                                        alt={name} />
                                    </Link>
                                  </div>
                                </Col>
                                <Col xs={12} sm={6}>
                                  <Link to={routes.artist(id)} className='lead flex-center'>
                                    {name}
                                  </Link>
                                </Col>
                              </Row>
                            </div>
                          </Col>
                        ))(partners)
                      }
                    </Row>
                  </Col>
                </Row> :
                null
            }
            {
              socialLinks && socialLinks.length ?
                <Row className='mt-4'>
                  <Col>
                    <h3>Social{socialLinks.length > 1 ? 's' : ''}</h3>
                    <Row className='justify-content-start no-gutters socials'>
                      {
                        fp.map(([key, value]) => (
                          <Col key={key} xs={2}>
                            <Button
                              color='link'
                              href={value}
                              target={key !== 'envelope' ? '_blank' : undefined}
                              className={`btn-${fp.get(key)(socials)} btn-floating btn-small waves-effect waves-light`}>
                              <Icon icon={key} />
                            </Button>
                          </Col>
                        ))(socialLinks)
                      }
                    </Row>
                  </Col>
                </Row> :
                null
            }
          </Col>
          {
            videos && videos.length ?
              <Col xs={12} lg={5}>
                <Row>
                  {
                    fp.flow(
                      fp.take(3),
                      noCapMap((src, index) => (
                        <Col
                          key={src}
                          className={classNames('my-3', { 'my-lg-0': !index })}
                          xs={12}
                          md={4}
                          lg={12}>
                          <Embed className='z-depth-1'>
                            <iframe src={src}
                              title={`${name} video ${index + 1}`}
                              allowFullScreen />
                          </Embed>
                        </Col>
                      ))
                    )(videos)
                  }
                </Row>
              </Col> :
              null
          }
        </Row>
        <Row>
          <Col>
            <h3 className='my-3'>About</h3>
            <Row tag='dl'>
              <Col tag='dt' xs={6} sm={4} md={3} lg={2}>
                Current Country
              </Col>
              <Col tag='dd' xs={6} sm={8} md={9} lg={10}>
                {country}
              </Col>
              {
                originalLocation ?
                  [
                    <Col key='x' tag='dt' xs={6} sm={4} md={3} lg={2}>
                      Original Country
                    </Col>,
                    <Col key='y' tag='dd' xs={6} sm={8} md={9} lg={10}>
                      {originalLocation.country}
                    </Col>
                  ] :
                  null
              }
              <Col tag='dt' xs={6} sm={4} md={3} lg={2}>
                Styles
              </Col>
              <Col tag='dd' xs={6} sm={8} md={9} lg={10}>
                {fp.join(', ')(styles)}
              </Col>
            </Row>
            <p dangerouslySetInnerHTML={{ __html: AllHtmlEntities.decode(bio) }} />
          </Col>
        </Row>
        <InstagramFeed data={instagramData} />
      </Container>
    </section>
  );

export default compose(
  graphql(artistQuery, {
    name: 'artistData',
    options: ({
      match: {
        params: {
          id
        }
      }
    }) => ({
      variables: {
        id
      }
    })
  }),
  hideIfNoArtistData('artistData'),
  notFoundIfNoArtistData('artistData'),
  branch(
    fp.get('artistData.artist.socialLinks.instagram'),
    graphql(instagramQuery, {
      name: 'instagramData',
      options: ({
        artistData: {
          artist: {
            socialLinks: {
              instagram
            }
          }
        }
      }) => ({
        variables: {
          username: fp.flow(
            (url) => new URL(url),
            fp.get('pathname'),
            fp.trimChars(['/'])
          )(instagram),
          queryArgs: ''
        }
      })
    })
  ),
  mapProps(({
    artistData: {
      artist: {
        socialLinks,
        email,
        website,
        ...artist
      },
      ...artistData
    },
    ...props
  }) => ({
    ...props,
    artistData: {
      ...artistData,
      artist: {
        ...artist,
        socialLinks: fp.flow(
          fp.toPairs,
          fp.filter(([key, value]) => value && !fp.startsWith('__')(key))
        )({
          ...socialLinks,
          facebook: fp.get('facebook.page')(socialLinks || {}),
          globe: website,
          envelope: email ?
            `mailto:${email}` :
            undefined
        })
      }
    }
  })),
  pure
)(Artist);



// WEBPACK FOOTER //
// ./src/components/pages/app/artist/artist.js